import { z } from "zod";
import { CHOICE_LABEL_MAX_LENGTH, QUESTION_DESCRIPTION_MAX_LENGTH, QUESTION_TITLE_MAX_LENGTH, RATING_POINT_LABEL_MAX_LENGTH, SECTION_NAME_MAX_LENGTH } from "./reviewConstraints.ts";
import { CycleReviewTypes } from "./reviewTypes.ts";

const QuestionKinds = ["text", "rating", "choice"] as const;

export type QuestionKind = (typeof QuestionKinds)[number];

const QuestionSharePolicies = ["hidden", "visible"] as const;

export type QuestionSharePolicy = (typeof QuestionSharePolicies)[number];

const QuestionAudienceSchema = z.object({
    isAsked: z.boolean(),
    title: z.string().max(QUESTION_TITLE_MAX_LENGTH),
    description: z.string().max(QUESTION_DESCRIPTION_MAX_LENGTH).optional(),
    sharePolicy: z.enum(QuestionSharePolicies).optional()
});

export type QuestionAudience = z.infer<typeof QuestionAudienceSchema>;

const QuestionAudiencesSchema = z.object({
    self: QuestionAudienceSchema,
    manager: QuestionAudienceSchema,
    peer: QuestionAudienceSchema,
    upward: QuestionAudienceSchema
});

export type QuestionAudiences = z.infer<typeof QuestionAudiencesSchema>;

const RatingPointSchema = z.object({
    value: z.number(),
    label: z.string().max(RATING_POINT_LABEL_MAX_LENGTH),
    description: z.string().max(QUESTION_DESCRIPTION_MAX_LENGTH).optional()
});

export type RatingPoint = z.infer<typeof RatingPointSchema>;

const ChoicesSchema = z.object({
    allowMultiple: z.boolean(),
    values: z.array(z.object({ value: z.number(), label: z.string().max(CHOICE_LABEL_MAX_LENGTH) }))
});

export type Choices = z.infer<typeof ChoicesSchema>;

const CycleQuestionnaireQuestionSchema = z.object({
    id: z.string(),
    kind: z.enum(QuestionKinds),
    isOptional: z.boolean(),
    audiences: QuestionAudiencesSchema,
    rephrased: z.object({ self: z.boolean(), peer: z.boolean(), upward: z.boolean() }),
    ratingScale: z.array(RatingPointSchema).optional(),
    choices: ChoicesSchema.optional()
});

export type CycleQuestionnaireQuestion = z.infer<typeof CycleQuestionnaireQuestionSchema>;

export const CycleQuestionnaireSectionSchema = z.object({
    id: z.string(),
    title: z.string().max(SECTION_NAME_MAX_LENGTH),
    description: z.string().max(QUESTION_DESCRIPTION_MAX_LENGTH).optional(),
    questions: z.array(CycleQuestionnaireQuestionSchema)
});

export type CycleQuestionnaireSection = z.infer<typeof CycleQuestionnaireSectionSchema>;

const QuestionUpsertSchema = z.object({
    audiences: QuestionAudiencesSchema,
    rephrased: z.object({ self: z.boolean(), peer: z.boolean(), upward: z.boolean() })
});

export const CycleQuestionnaireActionSchemas = [
    z.object({
        type: z.literal("AddReviewCycleSectionAction"),
        title: z.string().max(SECTION_NAME_MAX_LENGTH),
        description: z.string().max(QUESTION_DESCRIPTION_MAX_LENGTH)
    }),
    z.object({ type: z.literal("RemoveReviewCycleSectionAction"), sectionId: z.string() }),
    z.object({ type: z.literal("MoveReviewCycleSectionAction"), sectionId: z.string(), position: z.number().int().nonnegative() }),
    z.object({
        type: z.literal("UpdateReviewCycleSectionTitleAction"),
        sectionId: z.string(),
        title: z.string().max(SECTION_NAME_MAX_LENGTH)
    }),
    z.object({
        type: z.literal("UpdateReviewCycleSectionDescriptionAction"),
        sectionId: z.string(),
        description: z.string().max(QUESTION_DESCRIPTION_MAX_LENGTH)
    }),
    z.object({
        type: z.literal("AddReviewCycleQuestionAction"),
        sectionId: z.string(),
        kind: z.enum(QuestionKinds),
        isOptional: z.boolean(),
        question: QuestionUpsertSchema,
        ratingScale: z.array(RatingPointSchema).optional(),
        choices: ChoicesSchema.optional(),
        position: z.number().int().nonnegative().optional()
    }),
    z.object({ type: z.literal("RemoveReviewCycleQuestionAction"), questionId: z.string() }),
    z.object({
        type: z.literal("MoveReviewCycleQuestionAction"),
        questionId: z.string(),
        sectionId: z.string(),
        position: z.number().int().nonnegative()
    }),
    z.object({
        type: z.literal("UpdateReviewCycleQuestionTitleAction"),
        questionId: z.string(),
        audience: z.enum(CycleReviewTypes),
        title: z.string().max(QUESTION_TITLE_MAX_LENGTH)
    }),
    z.object({
        type: z.literal("UpdateReviewCycleQuestionDescriptionAction"),
        questionId: z.string(),
        audience: z.enum(CycleReviewTypes),
        description: z.string().max(QUESTION_DESCRIPTION_MAX_LENGTH)
    }),
    z.object({
        type: z.literal("UpdateReviewCycleQuestionAudienceAction"),
        questionId: z.string(),
        audience: z.enum(CycleReviewTypes),
        isEnabled: z.boolean()
    }),
    z.object({ type: z.literal("UpdateReviewCycleQuestionOptionalityAction"), questionId: z.string(), isOptional: z.boolean() }),
    z.object({
        type: z.literal("UpdateReviewCycleQuestionShareAnswerPolicyAction"),
        questionId: z.string(),
        audience: z.enum(CycleReviewTypes),
        sharePolicy: z.enum(QuestionSharePolicies).optional()
    }),
    z.object({
        type: z.literal("UpdateReviewCycleQuestionRephrasedAction"),
        questionId: z.string(),
        audience: z.enum(CycleReviewTypes),
        isRephrased: z.boolean()
    }),
    z.object({ type: z.literal("UpdateReviewCycleQuestionTypeAction"), questionId: z.string(), kind: z.enum(QuestionKinds) }),
    z.object({
        type: z.literal("UpdateReviewCycleQuestionRatingScaleAction"),
        questionId: z.string(),
        ratingScale: z.array(RatingPointSchema)
    }),
    z.object({ type: z.literal("UpdateReviewCycleQuestionChoicesAction"), questionId: z.string(), choices: ChoicesSchema })
] as const;
